import {AppState} from '@/store/state';
import {ToggleSidebarMenu} from '@/store/ui/actions';
import {UiState} from '@/store/ui/state';
import {Component, HostBinding, OnInit} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {AppService} from '@services/app.service';
import {RoleService} from '@services/role.service';
import {PowerBIMenuService} from '@services/powerbi-menu.service';
import {PowerBIEmbedService} from '@services/powerbi-embed.service';
import {Observable, filter} from 'rxjs';

const BASE_CLASSES = 'main-sidebar elevation-4';
const ADMIN_PROTECTED_ROUTES = ['/device-configuration-settings'];
@Component({
    selector: 'app-menu-sidebar',
    templateUrl: './menu-sidebar.component.html',
    styleUrls: ['./menu-sidebar.component.scss']
})
export class MenuSidebarComponent implements OnInit {
    @HostBinding('class') classes: string = BASE_CLASSES;
    public ui: Observable<UiState>;
    public user;
    public menu: any[] = [];
    public isPowerBiRoute = false;
    public tenantDomain = '';
    public activeDashboard = 'Operational';
    public isAdminUser = false;

    constructor(
        public appService: AppService,
        private store: Store<AppState>,
        private roleService: RoleService,
        private router: Router,
        public powerBiMenuService: PowerBIMenuService,
        private powerBiEmbedService: PowerBIEmbedService
    ) {}

    ngOnInit() {
        this.ui = this.store.select('ui');
        this.ui.subscribe((state: UiState) => {
            this.classes = `${BASE_CLASSES} ${state.sidebarSkin}`;
        });

        this.roleService.role$.subscribe((role) => {
            this.menu = this.filterMenuByRole(MENU, role || '');
            if (role && (role.toLowerCase().includes('admin') || role === '1')) {
                this.isAdminUser = true;
            }
        });

        this.powerBiEmbedService.getUserRoleInfo().subscribe({
            next: (res) => {
                if (res?.isAdmin) {
                    this.isAdminUser = true;
                }
            },
            error: () => {}
        });

        this.checkRoute(this.router.url);

        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.checkRoute(event.urlAfterRedirects || event.url);
            });

        this.powerBiMenuService.tenantDomain$.subscribe((domain) => {
            if (domain) {
                this.tenantDomain = domain;
            }
        });

        this.powerBiMenuService.selectDashboard$.subscribe((dashboardType) => {
            if (dashboardType) {
                this.activeDashboard = dashboardType;
            }
        });
    }

    private checkRoute(url: string): void {
        this.isPowerBiRoute = url.includes('/power-bi');
    }

    backToMainSite(): void {
        this.router.navigate(['/']);
    }

    selectDashboard(dashboardType: string): void {
        this.activeDashboard = dashboardType;
        this.powerBiMenuService.selectDashboard(dashboardType);
    }

    triggerPowerBiSettings(): void {
        this.powerBiMenuService.triggerPowerBiSettings();
    }

    triggerExport(): void {
        this.powerBiMenuService.triggerExport();
    }

    triggerRefreshSchedule(): void {
        this.powerBiMenuService.triggerRefreshSchedule();
    }

    triggerDownloadData(): void {
        this.powerBiMenuService.triggerDownloadData();
    }

    private filterMenuByRole(menuItems: any[], role: string): any[] {
        return menuItems
            .map((item) => {
                const clonedItem = {...item};

                if (clonedItem.roles && !clonedItem.roles.includes(role)) {
                    return null;
                }

                if (clonedItem.children && clonedItem.children.length > 0) {
                    clonedItem.children = clonedItem.children
                        .map((child) => {
                            if (child.roles && !child.roles.includes(role))
                                return null;
                            return child;
                        })
                        .filter(Boolean);
                }

                return clonedItem;
            })
            .filter(Boolean);
    }

    closeSidebar() {
        this.store.dispatch(new ToggleSidebarMenu());
    }
}

export const MENU = [
    {
        name: 'Dashboard',
        iconClasses: 'fas fa-chart-line',
        path: ['/']
    },
    {
        name: 'Power BI Dashboard',
        iconClasses: 'fas fa-chart-pie',
        path: ['/power-bi']
    },
    {
        name: 'Task List',
        iconClasses: 'fas fa-list-check',
        path: ['/task-list']
    },
    {
        name: 'Cycles',
        iconClasses: 'fas fa-recycle',
        path: ['/placement']
    },
    {
        name: 'Medication',
        iconClasses: 'fas fa-pills',
        path: ['/medication-list']
    },
    {
        name: 'Disinfection',
        iconClasses: 'fas fa-spray-can',
        path: ['/disinfection-list']
    },
    {
        name: 'Vaccine',
        iconClasses: 'fas fa-syringe',
        path: ['/vaccine-list']
    },
    {
        name: 'Notifications',
        iconClasses: 'fas fa-bell',
        path: ['/notification-list']
    },
    {
        name: 'Users Management',
        iconClasses: 'fas fa-users-cog',
        path: ['/farm-user-list']
    },
    {
        name: 'Farms Information',
        iconClasses: 'fas fa-feather',
        path: ['/farms-information'],
        roles: ['Admin', 'Top Management']
    },
    {
        name: 'Audit Log',
        iconClasses: 'fas fa-file-alt',
        path: ['/audit-log']
    },
    {
        name: 'Settings',
        iconClasses: 'fas fa-cog',
        children: [
            {
                name: 'User Profile',
                iconClasses: 'fas fa-user-cog',
                path: ['/user-settings']
            },
            {
                name: 'Company Information',
                iconClasses: 'fas fa-building',
                path: ['/company-information-settings']
            },
            {
                name: 'SmartPoultry Info & Legal',
                iconClasses: 'fas fa-info-circle',
                path: ['/smartPoultry-legal-info']
            },
            {
                name: 'Device Configuration',
                iconClasses: 'fas fa-sliders-h',
                path: ['/device-configuration-settings'],
                roles: ['Admin']
            }
        ]
    }
];
